import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/* 🔧 Normalize photos_json from API */
function normalizePhotos(photos_json) {
  if (!photos_json) return [];

  if (Array.isArray(photos_json)) {
    return photos_json.filter(Boolean);
  }

  if (typeof photos_json === "string") {
    try {
      const parsed = JSON.parse(photos_json);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch {
      if (photos_json.startsWith("http")) {
        return [photos_json];
      }
    }
  }

  return [];
}

/* ⏱ Static remaining time formatter (NO timers) */
function formatRemainingTime(auctionEndAt) {
  if (!auctionEndAt) return "—";

  const end = new Date(auctionEndAt);
  const now = new Date();
  const diffMs = end - now;

  if (isNaN(end.getTime()) || diffMs <= 0) {
    return "Auction ended";
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }

  return `${minutes}m left`;
}

export default function AvailableLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasVerifiedPayment, setHasVerifiedPayment] = useState(false);

  function logout() {
    localStorage.removeItem("vendor_token");
    localStorage.removeItem("vendor_id");
    navigate("/login");
  }

  useEffect(() => {
    const token = localStorage.getItem("vendor_token");
    if (!token) return navigate("/login");

    async function loadLeadsAndStatus() {
      try {
        // 🔑 Load payment verification status
        const statusRes = await fetch(
          "https://api.showmeyourjunk.biz/api/vendor/payment-status",
          {
            headers: { "x-vendor-token": token }
          }
        );

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setHasVerifiedPayment(!!statusData.has_verified_payment);
        }

        // 📦 Load available leads
        const response = await fetch(
          "https://api.showmeyourjunk.biz/api/vendor/available-estimates",
          {
            headers: { "x-vendor-token": token }
          }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          logout();
          return;
        }

        setLeads(data.estimates || []);
        setLoading(false);

      } catch (err) {
        console.error("Failed to load leads:", err);
        logout();
      }
    }

    loadLeadsAndStatus();
  }, [navigate]);

  if (loading) {
    return (
      <div style={page}>
        <h2>Loading leads…</h2>
      </div>
    );
  }

  return (
    <div style={page}>
      <button onClick={() => navigate("/dashboard")}>
        ← Back to Dashboard
      </button>

      <h1 style={{ marginTop: 10, marginBottom: 6 }}>Available Leads</h1>

      {!hasVerifiedPayment && (
        <div
          style={{
            marginTop: 8,
            marginBottom: 12,
            padding: 12,
            borderRadius: 8,
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            color: "#9a3412",
            fontSize: 14
          }}
        >
          <strong>Payment verification required:</strong>{" "}
          You can view lead details, but you must verify a payment method
          before placing bids.
        </div>
      )}

      <div style={disclaimer}>
        <strong>Exclusive Leads:</strong> Each lead can only be claimed by a
        single buyer and will not be resold to multiple vendors.
      </div>

      {leads.length === 0 ? (
        <p>No leads available at this moment.</p>
      ) : (
        <div style={grid}>
          {leads.map((lead) => {
            const photos = normalizePhotos(lead.photos_json);
            const thumbnail = photos[0] || null;

            const isNew = lead.lead_status === "new";

            const jobPrice = Number(lead.job_price);

            const estimatedWeight =
              lead.total_weight_lbs != null
                ? Number(lead.total_weight_lbs)
                : null;

            const estimatedVolume =
              lead.total_volume_cuft != null
                ? Number(lead.total_volume_cuft)
                : null;

            return (
              <div key={lead.estimate_id} style={card}>
                {/* IMAGE */}
                <div style={imageWrapper}>
                  <div
                    style={{
                      ...ribbon,
                      background: isNew ? "#16a34a" : "#6b7280"
                    }}
                  >
                    {isNew ? "NEW" : "CLAIMED"}
                  </div>

                  {!isNaN(jobPrice) && (
                    <div style={priceBadge}>
                      ${jobPrice.toFixed(2)}
                    </div>
                  )}

                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt="Junk items"
                      style={image}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div style={noImage}>No Image</div>
                  )}
                </div>

                {/* CONTENT */}
                <div style={content}>
                  <h3 style={estimateId}>
                    Customer Estimate<br />#{lead.estimate_id}
                  </h3>

                  <div style={meta}>Status: {lead.lead_status}</div>
                  <div style={meta}>Created: {lead.created_date}</div>

                  <div style={meta}>
                    Estimated Weight:{" "}
                    {estimatedWeight != null
                      ? `${estimatedWeight} lbs`
                      : "—"}
                  </div>

                  <div style={meta}>
                    Estimated Volume:{" "}
                    {estimatedVolume != null
                      ? `${estimatedVolume} cu ft`
                      : "—"}
                  </div>

                  {/* ⏱ STATIC AUCTION COUNTDOWN */}
                  <div style={{ ...meta, fontWeight: 600 }}>
                    Time left:{" "}
                    {formatRemainingTime(lead.auction_end_at)}
                  </div>

                  <button
                    style={button}
                    onClick={() =>
                      navigate(`/lead/${lead.estimate_id}`)
                    }
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  padding: 10,
  maxWidth: 1200,
  margin: "0 auto"
};

const disclaimer = {
  marginTop: 2,
  padding: 12,
  borderRadius: 8,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1e3a8a",
  fontSize: 14
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 16,
  marginTop: 16
};

const card = {
  display: "flex",
  flexDirection: "column",
  border: "1px solid #ddd",
  borderRadius: 10,
  overflow: "hidden",
  background: "#fff",
  position: "relative"
};

const imageWrapper = {
  width: "100%",
  aspectRatio: "1 / 1",
  background: "#f3f4f6",
  position: "relative",
  overflow: "hidden"
};

const image = {
  width: "100%",
  height: "100%",
  objectFit: "cover"
};

const noImage = {
  color: "#888",
  fontSize: 14,
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)"
};

const ribbon = {
  position: "absolute",
  top: 10,
  left: 10,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: "bold",
  color: "#fff",
  borderRadius: 4,
  zIndex: 2
};

const priceBadge = {
  position: "absolute",
  top: 10,
  right: 10,
  background: "#111827",
  color: "#fff",
  padding: "6px 10px",
  fontSize: 13,
  fontWeight: "bold",
  borderRadius: 999,
  zIndex: 2
};

const content = {
  padding: 14,
  display: "flex",
  flexDirection: "column",
  gap: 6
};

const estimateId = {
  fontSize: 16,
  margin: 0,
  wordBreak: "break-word"
};

const meta = {
  fontSize: 14,
  color: "#444"
};

const button = {
  marginTop: 10,
  padding: "10px 14px",
  fontSize: 14,
  borderRadius: 6,
  border: "none",
  background: "#2563eb",
  color: "#fff",
  cursor: "pointer"
};
